package br.com.htms.inspeccaocampo.data

import br.com.htms.inspeccaocampo.platform.currentTimeMillis

/**
 * Serialização JSON portável (sem org.json).
 */
object FormJson {
    fun toJson(form: FormInspecao): String = buildString {
        append('{')
        field("tstResponsavel", form.tstResponsavel); append(',')
        longField("dataHorarioMs", form.dataHorarioMs); append(',')
        if (form.latitude != null) {
            doubleField("latitude", form.latitude); append(',')
        }
        if (form.longitude != null) {
            doubleField("longitude", form.longitude); append(',')
        }
        field("localTexto", form.localTexto); append(',')
        field("obra", form.obra); append(',')
        field("responsavelFrente", form.responsavelFrente); append(',')
        field("descricaoAtividade", form.descricaoAtividade); append(',')
        field("efetivo", form.efetivo); append(',')
        append("\"desviosEncontrados\":"); append(secaoToJson(form.desviosEncontrados)); append(',')
        append("\"acoesCorretivas\":"); append(secaoToJson(form.acoesCorretivas)); append(',')
        field("informacoesAdicionais", form.informacoesAdicionais); append(',')
        append("\"informacoesAdicionaisFotos\":"); append(fotosToJson(form.informacoesAdicionaisFotos)); append(',')
        field("conclusao", form.conclusao); append(',')
        field("assinaturaSupervisor", form.assinaturaSupervisor); append(',')
        field("assinaturaSesmt", form.assinaturaSesmt); append(',')
        append("\"itensExtra\":"); append(itensExtraToJson(form.itensExtra)); append(',')
        append("\"itens\":[")
        form.itens.forEachIndexed { i, item ->
            if (i > 0) append(',')
            append('{')
            field("id", item.id); append(',')
            field("titulo", item.titulo); append(',')
            if (item.resposta != null) {
                field("resposta", item.resposta.name); append(',')
            } else {
                append("\"resposta\":null,")
            }
            field("justificativa", item.justificativa); append(',')
            append("\"fotos\":"); append(fotosToJson(item.fotos))
            append('}')
        }
        append(']')
        append('}')
    }

    fun fromJson(json: String): FormInspecao {
        val root = JsonMap.parse(json)
        val itensAtuais = InspecaoCatalog.itensPadrao()
        val porTitulo = itensAtuais.associateBy { it.titulo }.toMutableMap()
        val porId = itensAtuais.associateBy { it.id }

        root.array("itens")?.forEach { itemNode ->
            val item = itemNode as? JsonMap ?: return@forEach
            val titulo = item.str("titulo")
            val idSalvo = item.str("id")
            val base = porTitulo[titulo] ?: porId[idSalvo] ?: return@forEach
            val fotos = lerFotos(item)
            porTitulo[base.titulo] = base.copy(
                resposta = item.str("resposta").takeIf { it.isNotBlank() }
                    ?.let { runCatching { RespostaTernaria.valueOf(it) }.getOrNull() },
                fotos = fotos,
                justificativa = item.str("justificativa")
            )
        }

        val dataMs = root.long("dataHorarioMs") ?: currentTimeMillis()
        val localLegado = root.str("localResponsavel")
        val responsavel = root.str("responsavelFrente").ifBlank { localLegado }
        val localTexto = root.str("localTexto").ifBlank { localLegado }
        val desviosLegado = root.str("desvios")
        val acoesLegado = root.str("acoesCorretivas")

        var desvios = lerSecao(root, "desviosEncontrados")
        if (desvios.resposta == null && desviosLegado.isNotBlank()) {
            desvios = SecaoSimNao(RespostaSimNao.SIM, desviosLegado)
        }
        var acoes = lerSecao(root, "acoesCorretivas")
        if (acoes.resposta == null && acoesLegado.isNotBlank() && acoesLegado !in listOf("SIM", "NAO")) {
            // se for objeto JSON já parseado acima; legado string
            if (!root.raw.contains("\"acoesCorretivas\":{")) {
                acoes = SecaoSimNao(RespostaSimNao.SIM, acoesLegado)
            }
        }

        return FormInspecao(
            tstResponsavel = root.str("tstResponsavel"),
            dataHorarioMs = dataMs,
            latitude = root.double("latitude"),
            longitude = root.double("longitude"),
            localTexto = localTexto,
            obra = root.str("obra"),
            responsavelFrente = responsavel,
            descricaoAtividade = root.str("descricaoAtividade"),
            efetivo = root.str("efetivo"),
            itens = itensAtuais.map { porTitulo[it.titulo] ?: it },
            itensExtra = lerItensExtra(root),
            desviosEncontrados = desvios,
            acoesCorretivas = acoes,
            informacoesAdicionais = root.str("informacoesAdicionais"),
            informacoesAdicionaisFotos = lerFotosLista(root.array("informacoesAdicionaisFotos")),
            conclusao = root.str("conclusao"),
            assinaturaSupervisor = root.str("assinaturaSupervisor"),
            assinaturaSesmt = root.str("assinaturaSesmt")
        )
    }

    fun inspecaoToJson(inspecao: InspecaoCompleta): String = buildString {
        append('{')
        longField("id", inspecao.id); append(',')
        longField("criadoEm", inspecao.criadoEm); append(',')
        field("formTipo", inspecao.formTipo.id); append(',')
        append("\"enviadoRemoto\":"); append(inspecao.enviadoRemoto); append(',')
        append("\"form\":"); append(toJson(inspecao.form))
        if (inspecao.relatorioFoto != null) {
            append(',')
            append("\"relatorioFoto\":")
            append(relatorioToJson(inspecao.relatorioFoto))
        }
        if (inspecao.checklistAlojamento != null) {
            append(',')
            append("\"checklistAlojamento\":")
            append(checklistToJson(inspecao.checklistAlojamento))
        }
        if (inspecao.comunicacaoOcorrencias != null) {
            append(',')
            append("\"comunicacaoOcorrencias\":")
            append(comunicacaoToJson(inspecao.comunicacaoOcorrencias))
        }
        if (inspecao.checklistDiario != null) {
            append(',')
            append("\"checklistDiario\":")
            append(checklistDiarioToJson(inspecao.checklistDiario))
        }
        if (inspecao.analiseVisualNr != null) {
            append(',')
            append("\"analiseVisualNr\":")
            append(br.com.htms.inspeccaocampo.data.nrvisual.NrVisualJson.toJson(inspecao.analiseVisualNr))
        }
        append('}')
    }

    fun inspecaoFromJson(json: String): InspecaoCompleta {
        val root = JsonMap.parse(json)
        val formNode = root.obj("form")
        val formJson = formNode?.raw ?: "{}"
        val tipo = FormTipo.fromId(root.str("formTipo"))
        return InspecaoCompleta(
            id = root.long("id") ?: 0L,
            criadoEm = root.long("criadoEm") ?: currentTimeMillis(),
            formTipo = tipo,
            form = fromJson(formJson),
            relatorioFoto = root.obj("relatorioFoto")?.raw?.let { relatorioFromJson(it) },
            checklistAlojamento = root.obj("checklistAlojamento")?.raw?.let { raw ->
                val base = if (tipo == FormTipo.CHECKLIST_VETORES) {
                    ChecklistVetoresCatalog.itens()
                } else {
                    ChecklistAlojamentoCatalog.itensPadrao()
                }
                checklistFromJson(raw, base)
            },
            comunicacaoOcorrencias = root.obj("comunicacaoOcorrencias")?.raw?.let { comunicacaoFromJson(it) },
            checklistDiario = root.obj("checklistDiario")?.raw?.let { checklistDiarioFromJson(it) },
            analiseVisualNr = root.obj("analiseVisualNr")?.raw?.let {
                br.com.htms.inspeccaocampo.data.nrvisual.NrVisualJson.fromJson(it)
            },
            // JSON antigo sem o campo: não reenviar em massa (evita OOM offline).
            enviadoRemoto = root.boolOrNull("enviadoRemoto") ?: true
        )
    }

    fun relatorioToJson(form: FormRelatorioFoto): String = buildString {
        append('{')
        field("responsavelAlojamento", form.responsavelAlojamento); append(',')
        field("endereco", form.endereco); append(',')
        field("dataInicioContrato", form.dataInicioContrato); append(',')
        field("obra", form.obra); append(',')
        field("capacidadeTotalPessoas", form.capacidadeTotalPessoas); append(',')
        field("quantidadeFuncionarios", form.quantidadeFuncionarios); append(',')
        field("quantidadeQuartos", form.quantidadeQuartos); append(',')
        field("quantidadeCamas", form.quantidadeCamas); append(',')
        field("quantidadeArmarios", form.quantidadeArmarios); append(',')
        field("quantidadeBanheiros", form.quantidadeBanheiros); append(',')
        longField("dataHorarioMs", form.dataHorarioMs); append(',')
        if (form.latitude != null) {
            doubleField("latitude", form.latitude); append(',')
        }
        if (form.longitude != null) {
            doubleField("longitude", form.longitude); append(',')
        }
        field("localTexto", form.localTexto); append(',')
        field("observacoesGerais", form.observacoesGerais); append(',')
        append("\"observacoesFotos\":"); append(fotosToJson(form.observacoesFotos)); append(',')
        append("\"itens\":"); append(itensConformidadeToJson(form.itens))
        append('}')
    }

    fun relatorioFromJson(json: String): FormRelatorioFoto {
        val root = JsonMap.parse(json)
        val padrao = RelatorioFotoCatalog.itensPadrao().associateBy { it.id }.toMutableMap()
        var obsTexto = root.str("observacoesGerais")
        val obsFotos = lerFotosLista(root.array("observacoesFotos")).toMutableList()
        root.array("itens")?.forEach { node ->
            val item = node as? JsonMap ?: return@forEach
            val id = item.str("id")
            // Migrar antigo item 8.1 (Informações Adicionais) para Observações Gerais.
            if (id == "rf_8.1") {
                val just = item.str("justificativa")
                if (obsTexto.isBlank() && just.isNotBlank()) obsTexto = just
                obsFotos += lerFotos(item)
                return@forEach
            }
            val base = padrao[id] ?: return@forEach
            padrao[id] = base.copy(
                resposta = RespostaConformidade.fromName(item.str("resposta")),
                justificativa = item.str("justificativa"),
                valorExtra = item.str("valorExtra"),
                fotos = lerFotos(item)
            )
        }
        return FormRelatorioFoto(
            responsavelAlojamento = root.str("responsavelAlojamento"),
            endereco = root.str("endereco"),
            dataInicioContrato = root.str("dataInicioContrato"),
            obra = root.str("obra"),
            capacidadeTotalPessoas = root.str("capacidadeTotalPessoas"),
            quantidadeFuncionarios = root.str("quantidadeFuncionarios"),
            quantidadeQuartos = root.str("quantidadeQuartos"),
            quantidadeCamas = root.str("quantidadeCamas"),
            quantidadeArmarios = root.str("quantidadeArmarios"),
            quantidadeBanheiros = root.str("quantidadeBanheiros"),
            dataHorarioMs = root.long("dataHorarioMs") ?: currentTimeMillis(),
            latitude = root.double("latitude"),
            longitude = root.double("longitude"),
            localTexto = root.str("localTexto"),
            itens = RelatorioFotoCatalog.itensPadrao().map { padrao[it.id] ?: it },
            observacoesGerais = obsTexto,
            observacoesFotos = obsFotos
        )
    }

    fun checklistToJson(form: FormChecklistAlojamento): String = buildString {
        append('{')
        field("endereco", form.endereco); append(',')
        field("obra", form.obra); append(',')
        field("dataInspecao", form.dataInspecao); append(',')
        field("horaInspecao", form.horaInspecao); append(',')
        longField("dataHorarioMs", form.dataHorarioMs); append(',')
        if (form.latitude != null) {
            doubleField("latitude", form.latitude); append(',')
        }
        if (form.longitude != null) {
            doubleField("longitude", form.longitude); append(',')
        }
        field("localTexto", form.localTexto); append(',')
        field("numeroTrabalhadores", form.numeroTrabalhadores); append(',')
        field("dataVerificacao", form.dataVerificacao); append(',')
        if (form.liberadoParaUso != null) {
            field("liberadoParaUso", form.liberadoParaUso.name); append(',')
        } else {
            append("\"liberadoParaUso\":null,")
        }
        append("\"inspecionadoPor\":"); append(assinaturaChecklistToJson(form.inspecionadoPor)); append(',')
        append("\"acompanhadoPor\":"); append(assinaturaChecklistToJson(form.acompanhadoPor)); append(',')
        append("\"engenheiroResidente\":"); append(assinaturaChecklistToJson(form.engenheiroResidente)); append(',')
        append("\"acoesCorrecao\":[")
        form.acoesCorrecao.forEachIndexed { i, a ->
            if (i > 0) append(',')
            append('{')
            field("item", a.item); append(',')
            field("acao", a.acao); append(',')
            field("responsavel", a.responsavel); append(',')
            field("prazo", a.prazo)
            append('}')
        }
        append("],")
        append("\"itens\":"); append(itensConformidadeToJson(form.itens))
        append('}')
    }

    fun checklistFromJson(
        json: String,
        itensBase: List<ItemConformidade> = ChecklistAlojamentoCatalog.itensPadrao()
    ): FormChecklistAlojamento {
        val root = JsonMap.parse(json)
        val padrao = itensBase.associateBy { it.id }.toMutableMap()
        root.array("itens")?.forEach { node ->
            val item = node as? JsonMap ?: return@forEach
            val id = item.str("id")
            val base = padrao[id] ?: return@forEach
            padrao[id] = base.copy(
                resposta = RespostaConformidade.fromName(item.str("resposta")),
                justificativa = item.str("justificativa"),
                valorExtra = item.str("valorExtra"),
                fotos = lerFotos(item)
            )
        }
        val acoes = root.array("acoesCorrecao")?.mapNotNull { node ->
            val a = node as? JsonMap ?: return@mapNotNull null
            AcaoCorrecaoChecklist(
                item = a.str("item"),
                acao = a.str("acao"),
                responsavel = a.str("responsavel"),
                prazo = a.str("prazo")
            )
        }.orEmpty()
        return FormChecklistAlojamento(
            endereco = root.str("endereco"),
            obra = root.str("obra"),
            dataInspecao = root.str("dataInspecao"),
            horaInspecao = root.str("horaInspecao"),
            dataHorarioMs = root.long("dataHorarioMs") ?: currentTimeMillis(),
            latitude = root.double("latitude"),
            longitude = root.double("longitude"),
            localTexto = root.str("localTexto"),
            itens = itensBase.map { padrao[it.id] ?: it },
            numeroTrabalhadores = root.str("numeroTrabalhadores"),
            dataVerificacao = root.str("dataVerificacao"),
            liberadoParaUso = root.str("liberadoParaUso").takeIf { it.isNotBlank() }
                ?.let { runCatching { RespostaSimNao.valueOf(it) }.getOrNull() },
            acoesCorrecao = acoes,
            inspecionadoPor = lerAssinaturaChecklist(root.obj("inspecionadoPor")),
            acompanhadoPor = lerAssinaturaChecklist(root.obj("acompanhadoPor")),
            engenheiroResidente = lerAssinaturaChecklist(root.obj("engenheiroResidente"))
        )
    }

    fun comunicacaoToJson(form: FormComunicacaoOcorrencias): String = buildString {
        append('{')
        field("cliente", form.cliente); append(',')
        field("obra", form.obra); append(',')
        if (form.caracterizacao != null) {
            field("caracterizacao", form.caracterizacao.name); append(',')
        } else {
            append("\"caracterizacao\":null,")
        }
        if (form.tipo != null) {
            field("tipo", form.tipo.name); append(',')
        } else {
            append("\"tipo\":null,")
        }
        field("dataHoraOcorrido", form.dataHoraOcorrido); append(',')
        field("localExato", form.localExato); append(',')
        field("quemReportando", form.quemReportando); append(',')
        if (form.tipoEvento != null) {
            field("tipoEvento", form.tipoEvento.name); append(',')
        } else {
            append("\"tipoEvento\":null,")
        }
        field("envolvidoNome", form.envolvidoNome); append(',')
        field("envolvidoFuncao", form.envolvidoFuncao); append(',')
        field("responsavelAtividade", form.responsavelAtividade); append(',')
        field("responsavelCargo", form.responsavelCargo); append(',')
        if (form.responsavelEstavaNoLocal != null) {
            field("responsavelEstavaNoLocal", form.responsavelEstavaNoLocal.name); append(',')
        } else {
            append("\"responsavelEstavaNoLocal\":null,")
        }
        field("dataAcidente", form.dataAcidente); append(',')
        field("horaAcidente", form.horaAcidente); append(',')
        field("horasTrabalhadas", form.horasTrabalhadas); append(',')
        field("localAcidente", form.localAcidente); append(',')
        field("parteCorpoLesionada", form.parteCorpoLesionada); append(',')
        field("agenteCausador", form.agenteCausador); append(',')
        field("naturezaLesao", form.naturezaLesao); append(',')
        field("acoesTomadasImediato", form.acoesTomadasImediato); append(',')
        field("descricaoEvento", form.descricaoEvento); append(',')
        longField("dataHorarioMs", form.dataHorarioMs); append(',')
        if (form.latitude != null) {
            doubleField("latitude", form.latitude); append(',')
        }
        if (form.longitude != null) {
            doubleField("longitude", form.longitude); append(',')
        }
        field("localTexto", form.localTexto); append(',')
        append("\"fotos\":"); append(fotosToJson(form.fotos))
        append('}')
    }

    fun comunicacaoFromJson(json: String): FormComunicacaoOcorrencias {
        val root = JsonMap.parse(json)
        return FormComunicacaoOcorrencias(
            cliente = root.str("cliente"),
            obra = root.str("obra"),
            caracterizacao = CaracterizacaoOcorrencia.fromName(root.str("caracterizacao")),
            tipo = TipoOcorrencia.fromName(root.str("tipo")),
            dataHoraOcorrido = root.str("dataHoraOcorrido"),
            localExato = root.str("localExato"),
            quemReportando = root.str("quemReportando"),
            tipoEvento = ClasseEventoOcorrencia.fromName(root.str("tipoEvento")),
            envolvidoNome = root.str("envolvidoNome"),
            envolvidoFuncao = root.str("envolvidoFuncao"),
            responsavelAtividade = root.str("responsavelAtividade"),
            responsavelCargo = root.str("responsavelCargo"),
            responsavelEstavaNoLocal = root.str("responsavelEstavaNoLocal").takeIf { it.isNotBlank() }
                ?.let { runCatching { RespostaSimNao.valueOf(it) }.getOrNull() },
            dataAcidente = root.str("dataAcidente"),
            horaAcidente = root.str("horaAcidente"),
            horasTrabalhadas = root.str("horasTrabalhadas"),
            localAcidente = root.str("localAcidente"),
            parteCorpoLesionada = root.str("parteCorpoLesionada"),
            agenteCausador = root.str("agenteCausador"),
            naturezaLesao = root.str("naturezaLesao"),
            acoesTomadasImediato = root.str("acoesTomadasImediato"),
            descricaoEvento = root.str("descricaoEvento"),
            fotos = lerFotosLista(root.array("fotos")),
            dataHorarioMs = root.long("dataHorarioMs") ?: currentTimeMillis(),
            latitude = root.double("latitude"),
            longitude = root.double("longitude"),
            localTexto = root.str("localTexto")
        )
    }

    fun checklistDiarioToJson(form: br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario): String =
        buildString {
            append('{')
            field("marca", form.marca); append(',')
            field("tipoId", form.tipoId); append(',')
            field("tipoNome", form.tipoNome); append(',')
            field("tipoTitulo", form.tipoTitulo); append(',')
            field("obra", form.obra); append(',')
            field("mesAno", form.mesAno); append(',')
            field("numeroChecklist", form.numeroChecklist); append(',')
            longField("dataHorarioMs", form.dataHorarioMs); append(',')
            field("proprietario", form.proprietario); append(',')
            field("modelo", form.modelo); append(',')
            field("placa", form.placa); append(',')
            field("combustivel", form.combustivel); append(',')
            field("motoristaNome", form.motoristaNome); append(',')
            field("motoristaFuncao", form.motoristaFuncao); append(',')
            field("motoristaCnh", form.motoristaCnh); append(',')
            field("motoristaValidade", form.motoristaValidade); append(',')
            field("preenchidoPor", form.preenchidoPor); append(',')
            field("fornecedorCliente", form.fornecedorCliente); append(',')
            field("contrato", form.contrato); append(',')
            field("validadeContrato", form.validadeContrato); append(',')
            field("gestorContrato", form.gestorContrato); append(',')
            field("localidadeMobilizacao", form.localidadeMobilizacao); append(',')
            field("areaAcesso", form.areaAcesso); append(',')
            field("anoFabricacao", form.anoFabricacao); append(',')
            field("tipoVeiculo", form.tipoVeiculo); append(',')
            field("observacoesGerais", form.observacoesGerais); append(',')
            if (form.aprovado != null) {
                boolField("aprovado", form.aprovado); append(',')
            } else {
                append("\"aprovado\":null,")
            }
            append("\"fotosPlanoManutencao\":"); append(fotosToJson(form.fotosPlanoManutencao)); append(',')
            append("\"fotosEvidenciasManutencao\":"); append(fotosToJson(form.fotosEvidenciasManutencao)); append(',')
            append("\"fotosCrlv\":"); append(fotosToJson(form.fotosCrlv)); append(',')
            append("\"fotosObservacoes\":"); append(fotosToJson(form.fotosObservacoes)); append(',')
            if (form.latitude != null) {
                doubleField("latitude", form.latitude); append(',')
            }
            if (form.longitude != null) {
                doubleField("longitude", form.longitude); append(',')
            }
            field("localTexto", form.localTexto); append(',')
            field("assinaturaMotorista", form.assinaturaMotorista); append(',')
            field("assinaturaEncarregado", form.assinaturaEncarregado); append(',')
            field("assinaturaSesmt", form.assinaturaSesmt); append(',')
            append("\"itens\":"); append(itensConformidadeToJson(form.itens))
            append('}')
        }

    fun checklistDiarioFromJson(json: String): br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario {
        val root = JsonMap.parse(json)
        val itens = root.array("itens")?.mapNotNull { node ->
            val item = node as? JsonMap ?: return@mapNotNull null
            ItemConformidade(
                id = item.str("id").ifBlank { "cd_${br.com.htms.inspeccaocampo.platform.randomUuid()}" },
                numero = item.str("numero"),
                titulo = item.str("titulo"),
                secao = item.str("secao"),
                resposta = RespostaConformidade.fromName(item.str("resposta")),
                justificativa = item.str("justificativa"),
                valorExtra = item.str("valorExtra"),
                fotos = lerFotos(item)
            )
        }.orEmpty()
        val aprovado = root.boolOrNull("aprovado")
        return br.com.htms.inspeccaocampo.data.checklist.FormChecklistDiario(
            marca = root.str("marca").ifBlank { "htms" },
            tipoId = root.str("tipoId"),
            tipoNome = root.str("tipoNome"),
            tipoTitulo = root.str("tipoTitulo"),
            obra = root.str("obra"),
            mesAno = root.str("mesAno"),
            numeroChecklist = root.str("numeroChecklist").ifBlank { "1" },
            dataHorarioMs = root.long("dataHorarioMs") ?: currentTimeMillis(),
            proprietario = root.str("proprietario"),
            modelo = root.str("modelo"),
            placa = root.str("placa"),
            combustivel = root.str("combustivel"),
            motoristaNome = root.str("motoristaNome"),
            motoristaFuncao = root.str("motoristaFuncao"),
            motoristaCnh = root.str("motoristaCnh"),
            motoristaValidade = root.str("motoristaValidade"),
            preenchidoPor = root.str("preenchidoPor"),
            fornecedorCliente = root.str("fornecedorCliente"),
            contrato = root.str("contrato"),
            validadeContrato = root.str("validadeContrato"),
            gestorContrato = root.str("gestorContrato"),
            localidadeMobilizacao = root.str("localidadeMobilizacao"),
            areaAcesso = root.str("areaAcesso"),
            anoFabricacao = root.str("anoFabricacao"),
            tipoVeiculo = root.str("tipoVeiculo"),
            observacoesGerais = root.str("observacoesGerais"),
            aprovado = aprovado,
            fotosPlanoManutencao = lerFotosLista(root.array("fotosPlanoManutencao")),
            fotosEvidenciasManutencao = lerFotosLista(root.array("fotosEvidenciasManutencao")),
            fotosCrlv = lerFotosLista(root.array("fotosCrlv")),
            fotosObservacoes = lerFotosLista(root.array("fotosObservacoes")),
            itens = itens,
            latitude = root.double("latitude"),
            longitude = root.double("longitude"),
            localTexto = root.str("localTexto"),
            assinaturaMotorista = root.str("assinaturaMotorista"),
            assinaturaEncarregado = root.str("assinaturaEncarregado"),
            assinaturaSesmt = root.str("assinaturaSesmt")
        )
    }

    private fun itensConformidadeToJson(itens: List<ItemConformidade>): String = buildString {
        append('[')
        itens.forEachIndexed { i, item ->
            if (i > 0) append(',')
            append('{')
            field("id", item.id); append(',')
            field("numero", item.numero); append(',')
            field("titulo", item.titulo); append(',')
            field("secao", item.secao); append(',')
            if (item.resposta != null) field("resposta", item.resposta.name)
            else append("\"resposta\":null")
            append(',')
            field("justificativa", item.justificativa); append(',')
            field("valorExtra", item.valorExtra); append(',')
            append("\"fotos\":"); append(fotosToJson(item.fotos))
            append('}')
        }
        append(']')
    }

    private fun assinaturaChecklistToJson(a: AssinaturaChecklist): String = buildString {
        append('{')
        field("nome", a.nome); append(',')
        field("data", a.data); append(',')
        field("assinatura", a.assinatura)
        append('}')
    }

    private fun lerAssinaturaChecklist(obj: JsonMap?): AssinaturaChecklist {
        if (obj == null) return AssinaturaChecklist()
        return AssinaturaChecklist(
            nome = obj.str("nome"),
            data = obj.str("data"),
            assinatura = obj.str("assinatura")
        )
    }

    fun configToJson(config: AppConfig): String = buildString {
        append('{')
        field("nomeApp", config.nomeApp); append(',')
        field("nomeInspecao", config.nomeInspecao); append(',')
        if (config.bannerPath != null) {
            field("bannerPath", config.bannerPath); append(',')
        } else {
            append("\"bannerPath\":null,")
        }
        append("\"campos\":"); append(camposConfigToJson(config.campos)); append(',')
        append("\"camposAlojamentoChecklist\":")
        append(camposConfigToJson(config.camposAlojamentoChecklist)); append(',')
        append("\"camposAlojamentoFoto\":")
        append(camposConfigToJson(config.camposAlojamentoFoto)); append(',')
        append("\"camposOcorrencias\":")
        append(camposConfigToJson(config.camposOcorrencias)); append(',')
        append("\"formAcessos\":")
        append(formAcessosToJson(config.formAcessos)); append(',')
        append("\"tags\":")
        append(tagsToJson(config.tagsSanitizadas())); append(',')
        append("\"colabEscopos\":")
        append(colabEscoposToJson(config.colabEscopos)); append(',')
        field("emailDestinoTst", config.emailDestinoTst); append(',')
        field("emailDestinoAdm", config.emailDestinoAdm); append(',')
        append("\"emissaoDocUsuarios\":[")
        config.emissaoDocUsuarios.forEachIndexed { i, u ->
            if (i > 0) append(',')
            append('"').append(escape(u)).append('"')
        }
        append("],\"ativosUsuarios\":[")
        config.ativosUsuarios.forEachIndexed { i, u ->
            if (i > 0) append(',')
            append('"').append(escape(u)).append('"')
        }
        append(']')
        append('}')
    }

    private fun formAcessosToJson(acessos: List<FormAcessoConfig>): String = buildString {
        append('[')
        acessos.forEachIndexed { i, a ->
            if (i > 0) append(',')
            append('{')
            field("formId", a.formId); append(',')
            boolField("tst", a.tst); append(',')
            boolField("adm", a.adm); append(',')
            boolField("supervisor", a.supervisor); append(',')
            boolField("engenheiro", a.engenheiro); append(',')
            field("emailDestino", a.emailDestino.id); append(',')
            field("emailsExtras", a.emailsExtras)
            append('}')
        }
        append(']')
    }

    private fun tagsToJson(tags: List<TagConfig>): String = buildString {
        append('[')
        tags.forEachIndexed { i, tag ->
            if (i > 0) append(',')
            append('{')
            field("id", tag.id); append(',')
            field("rotulo", tag.rotulo)
            append('}')
        }
        append(']')
    }

    private fun colabEscoposToJson(escopos: List<br.com.htms.inspeccaocampo.data.colaboradores.ColabEscopoTag>): String = buildString {
        append('[')
        escopos.forEachIndexed { i, e ->
            if (i > 0) append(',')
            append('{')
            field("tagId", e.tagId); append(',')
            boolField("limitado", e.limitado); append(',')
            append("\"abas\":[")
            e.abas.forEachIndexed { j, aba ->
                if (j > 0) append(',')
                append('"').append(escape(aba)).append('"')
            }
            append("],\"docs\":[")
            e.docs.forEachIndexed { j, doc ->
                if (j > 0) append(',')
                append('"').append(escape(doc)).append('"')
            }
            append("],")
            boolField("somenteVer", e.somenteVer); append(',')
            boolField("filtrarCcProprio", e.filtrarCcProprio)
            append('}')
        }
        append(']')
    }

    private fun lerColabEscopos(root: JsonMap): List<br.com.htms.inspeccaocampo.data.colaboradores.ColabEscopoTag> {
        val arr = root.array("colabEscopos") ?: return emptyList()
        return arr.mapNotNull { node ->
            val o = node as? JsonMap ?: return@mapNotNull null
            val id = TagConfig.normalizarId(o.str("tagId"))
            if (!TagConfig.idValido(id)) return@mapNotNull null
            br.com.htms.inspeccaocampo.data.colaboradores.ColabEscopo.sanitizar(
                br.com.htms.inspeccaocampo.data.colaboradores.ColabEscopoTag(
                    tagId = id,
                    limitado = o.bool("limitado"),
                    abas = o.strList("abas"),
                    docs = o.strList("docs"),
                    somenteVer = o.bool("somenteVer", true),
                    filtrarCcProprio = o.bool("filtrarCcProprio", id == "TST")
                )
            )
        }.distinctBy { it.tagId }
    }

    private fun lerTags(root: JsonMap): List<TagConfig> {
        val arr = root.array("tags") ?: return TagConfig.padrao()
        val list = arr.mapNotNull { node ->
            val o = node as? JsonMap ?: return@mapNotNull null
            val id = TagConfig.normalizarId(o.str("id"))
            if (!TagConfig.idValido(id)) return@mapNotNull null
            TagConfig(id, o.str("rotulo").trim().take(40).ifBlank { id })
        }.distinctBy { it.id }
        return list.ifEmpty { TagConfig.padrao() }
    }

    private fun lerFormAcessos(root: JsonMap): List<FormAcessoConfig> {
        val list = root.array("formAcessos")?.mapNotNull { node ->
            val o = node as? JsonMap ?: return@mapNotNull null
            val id = o.str("formId").ifBlank { return@mapNotNull null }
            FormAcessoConfig(
                formId = id,
                tst = o.bool("tst", true),
                adm = o.bool("adm", false),
                supervisor = o.bool("supervisor", false),
                engenheiro = o.bool("engenheiro", false),
                emailDestino = FormEmailDestino.fromId(o.str("emailDestino")),
                emailsExtras = o.str("emailsExtras")
            )
        }.orEmpty()
        if (list.isEmpty()) return FormAcessoConfig.listaPadraoHub()
        return FormAcessoConfig.completarLista(list)
    }

    private fun camposConfigToJson(campos: List<CampoConfig>): String = buildString {
        append('[')
        campos.forEachIndexed { i, c ->
            if (i > 0) append(',')
            append('{')
            field("id", c.id); append(',')
            field("titulo", c.titulo); append(',')
            boolField("ativo", c.ativo); append(',')
            boolField("obrigatorio", c.obrigatorio); append(',')
            boolField("fotoObrigatoriaSeSim", c.fotoObrigatoriaSeSim); append(',')
            boolField("fotoObrigatoriaSeNao", c.fotoObrigatoriaSeNao)
            append('}')
        }
        append(']')
    }

    private fun lerCamposConfig(
        root: JsonMap,
        key: String,
        padrao: () -> List<CampoConfig>
    ): List<CampoConfig> {
        val list = root.array(key)?.mapNotNull { node ->
            val c = node as? JsonMap ?: return@mapNotNull null
            CampoConfig(
                id = c.str("id").ifBlank { "campo_${br.com.htms.inspeccaocampo.platform.randomUuid()}" },
                titulo = c.str("titulo"),
                ativo = c.bool("ativo", true),
                obrigatorio = c.bool("obrigatorio", true),
                fotoObrigatoriaSeSim = c.bool("fotoObrigatoriaSeSim", true),
                fotoObrigatoriaSeNao = c.bool("fotoObrigatoriaSeNao", true)
            )
        }.orEmpty()
        return list.ifEmpty { padrao() }
    }

    fun configFromJson(json: String): AppConfig {
        val root = JsonMap.parse(json)
        val campos = lerCamposConfig(root, "campos") { AppConfig.camposPadrao() }
        return AppConfig(
            nomeApp = root.str("nomeApp").ifBlank { "HTMS ENERGIA" },
            nomeInspecao = root.str("nomeInspecao").ifBlank { "Portal QSSMA" }
                .let { if (it == "Inspeção de Campo Diária") "Portal QSSMA" else it },
            bannerPath = root.str("bannerPath").takeIf { it.isNotBlank() },
            campos = campos,
            camposAlojamentoChecklist = lerCamposConfig(root, "camposAlojamentoChecklist") {
                AppConfig.camposAlojamentoChecklistPadrao()
            },
            camposAlojamentoFoto = lerCamposConfig(root, "camposAlojamentoFoto") {
                AppConfig.camposAlojamentoFotoPadrao()
            },
            camposOcorrencias = lerCamposConfig(root, "camposOcorrencias") {
                AppConfig.camposOcorrenciasPadrao()
            },
            formAcessos = lerFormAcessos(root),
            tags = lerTags(root),
            colabEscopos = lerColabEscopos(root),
            emailDestinoTst = root.str("emailDestinoTst").ifBlank { "admqsms@htmsgroup.com" },
            emailDestinoAdm = root.str("emailDestinoAdm").ifBlank { "logistica@htmsgroup.com" },
            emissaoDocUsuarios = root.strList("emissaoDocUsuarios")
                .map { it.trim().lowercase() }
                .filter { it.isNotEmpty() }
                .distinct(),
            ativosUsuarios = root.strList("ativosUsuarios")
                .map { it.trim().lowercase() }
                .filter { it.isNotEmpty() }
                .distinct()
        )
    }

    private fun secaoToJson(secao: SecaoSimNao): String = buildString {
        append('{')
        if (secao.resposta != null) field("resposta", secao.resposta.name) else append("\"resposta\":null")
        append(',')
        field("descricao", secao.descricao); append(',')
        append("\"fotos\":"); append(fotosToJson(secao.fotos))
        append('}')
    }

    private fun itensExtraToJson(itens: List<ItemCampoPersonalizado>): String = buildString {
        append('[')
        itens.forEachIndexed { i, item ->
            if (i > 0) append(',')
            append(itemPersonalizadoToJson(item))
        }
        append(']')
    }

    private fun itemPersonalizadoToJson(item: ItemCampoPersonalizado): String = buildString {
        append('{')
        field("id", item.id); append(',')
        field("titulo", item.titulo); append(',')
        if (item.resposta != null) {
            field("resposta", item.resposta.name)
        } else {
            append("\"resposta\":null")
        }
        append(',')
        field("justificativa", item.justificativa); append(',')
        append("\"fotos\":"); append(fotosToJson(item.fotos))
        append('}')
    }

    private fun lerItensExtra(root: JsonMap): List<ItemCampoPersonalizado> {
        val arr = root.array("itensExtra")
        if (arr != null) {
            val lidos = arr.mapNotNull { node ->
                val obj = node as? JsonMap ?: return@mapNotNull null
                lerItemPersonalizado(obj)
            }
            return lidos.ifEmpty { listOf(ItemCampoPersonalizado()) }
        }
        // Legado: um único itemPersonalizado (C/NC/NA ou ternário).
        val legado = root.obj("itemPersonalizado")
        if (legado != null) {
            return listOf(lerItemPersonalizado(legado))
        }
        return listOf(ItemCampoPersonalizado())
    }

    private fun lerItemPersonalizado(obj: JsonMap?): ItemCampoPersonalizado {
        if (obj == null) return ItemCampoPersonalizado()
        val raw = obj.str("resposta").takeIf { it.isNotBlank() }
        val resposta = when (raw) {
            null -> null
            "C", "SIM" -> RespostaTernaria.SIM
            "NC", "NAO" -> RespostaTernaria.NAO
            "NA" -> RespostaTernaria.NA
            else -> runCatching { RespostaTernaria.valueOf(raw) }.getOrNull()
                ?: RespostaConformidade.fromName(raw)?.let {
                    when (it) {
                        RespostaConformidade.C -> RespostaTernaria.SIM
                        RespostaConformidade.NC -> RespostaTernaria.NAO
                        RespostaConformidade.NA -> RespostaTernaria.NA
                    }
                }
        }
        return ItemCampoPersonalizado(
            id = obj.str("id").ifBlank { br.com.htms.inspeccaocampo.platform.randomUuid() },
            titulo = obj.str("titulo"),
            resposta = resposta,
            justificativa = obj.str("justificativa"),
            fotos = lerFotos(obj)
        )
    }

    private fun fotosToJson(fotos: List<FotoAnexo>): String = buildString {
        append('[')
        fotos.forEachIndexed { i, f ->
            if (i > 0) append(',')
            append('{')
            field("id", f.id); append(',')
            field("path", f.path); append(',')
            longField("capturadoEm", f.capturadoEm)
            if (f.latitude != null) {
                append(',')
                doubleField("latitude", f.latitude)
            }
            if (f.longitude != null) {
                append(',')
                doubleField("longitude", f.longitude)
            }
            val b64 = f.conteudoBase64
            if (!b64.isNullOrBlank()) {
                append(',')
                field("conteudoBase64", b64)
            }
            append('}')
        }
        append(']')
    }

    private fun lerSecao(root: JsonMap, key: String): SecaoSimNao {
        val obj = root.obj(key) ?: return SecaoSimNao()
        return SecaoSimNao(
            resposta = obj.str("resposta").takeIf { it.isNotBlank() }
                ?.let { runCatching { RespostaSimNao.valueOf(it) }.getOrNull() },
            descricao = obj.str("descricao"),
            fotos = lerFotos(obj)
        )
    }

    private fun lerFotos(obj: JsonMap): List<FotoAnexo> = lerFotosLista(obj.array("fotos"))

    private fun lerFotosLista(arr: List<Any?>?): List<FotoAnexo> {
        return arr?.mapNotNull { node ->
            val f = node as? JsonMap ?: return@mapNotNull null
            val path = f.str("path")
            if (path.isBlank()) return@mapNotNull null
            FotoAnexo(
                id = f.str("id").ifBlank { br.com.htms.inspeccaocampo.platform.randomUuid() },
                path = path,
                capturadoEm = f.long("capturadoEm") ?: currentTimeMillis(),
                latitude = f.double("latitude"),
                longitude = f.double("longitude"),
                conteudoBase64 = f.str("conteudoBase64").takeIf { it.isNotBlank() }
            )
        }.orEmpty()
    }

    private fun StringBuilder.field(key: String, value: String) {
        append('"').append(key).append("\":\"").append(escape(value)).append('"')
    }

    private fun StringBuilder.longField(key: String, value: Long) {
        append('"').append(key).append("\":").append(value)
    }

    private fun StringBuilder.doubleField(key: String, value: Double) {
        append('"').append(key).append("\":").append(value)
    }

    private fun StringBuilder.boolField(key: String, value: Boolean) {
        append('"').append(key).append("\":").append(value)
    }

    private fun escape(s: String): String = buildString(s.length + 8) {
        for (c in s) {
            when (c) {
                '\\' -> append("\\\\")
                '"' -> append("\\\"")
                '\n' -> append("\\n")
                '\r' -> append("\\r")
                '\t' -> append("\\t")
                else -> if (c.code < 0x20) {
                    append("\\u")
                    append(c.code.toString(16).padStart(4, '0'))
                } else append(c)
            }
        }
    }
}

/** Parser JSON mínimo para objetos/arrays usados pelo app. */
internal class JsonMap(val raw: String, private val map: Map<String, Any?>) {
    fun str(key: String): String = map[key] as? String ?: ""
    fun long(key: String): Long? = when (val v = map[key]) {
        is Long -> v
        is Int -> v.toLong()
        is Double -> v.toLong()
        is String -> v.toLongOrNull()
        else -> null
    }
    fun double(key: String): Double? = when (val v = map[key]) {
        is Double -> v
        is Long -> v.toDouble()
        is Int -> v.toDouble()
        is String -> v.toDoubleOrNull()
        else -> null
    }
    fun bool(key: String, default: Boolean = false): Boolean = when (val v = map[key]) {
        is Boolean -> v
        else -> default
    }

    fun boolOrNull(key: String): Boolean? = when (val v = map[key]) {
        is Boolean -> v
        null -> null
        else -> null
    }
    fun obj(key: String): JsonMap? = map[key] as? JsonMap
    fun array(key: String): List<Any?>? = map[key] as? List<*>
    fun strList(key: String): List<String> =
        array(key)?.mapNotNull { it as? String } ?: emptyList()

    fun stringValues(): Map<String, String> = buildMap {
        for ((k, v) in map) {
            when (v) {
                is String -> put(k, v)
                is Number -> put(k, v.toString())
                is Boolean -> put(k, v.toString())
            }
        }
    }

    companion object {
        fun parse(json: String): JsonMap {
            val trimmed = json.trim()
            val value = JsonParser(trimmed).parseValue()
            return value as? JsonMap ?: JsonMap("{}", emptyMap())
        }
    }
}

private class JsonParser(private val s: String) {
    private var i = 0

    fun parseValue(): Any? {
        skipWs()
        if (i >= s.length) return null
        return when (s[i]) {
            '{' -> parseObject()
            '[' -> parseArray()
            '"' -> parseString()
            't' -> { expect("true"); true }
            'f' -> { expect("false"); false }
            'n' -> { expect("null"); null }
            '-', in '0'..'9' -> parseNumber()
            else -> null
        }
    }

    private fun parseObject(): JsonMap {
        val start = i
        expect('{')
        val map = linkedMapOf<String, Any?>()
        skipWs()
        if (peek() == '}') {
            i++
            return JsonMap(s.substring(start, i), map)
        }
        while (true) {
            skipWs()
            val key = parseString()
            skipWs()
            expect(':')
            val value = parseValue()
            map[key] = value
            skipWs()
            when (peek()) {
                ',' -> i++
                '}' -> {
                    i++
                    break
                }
                else -> break
            }
        }
        return JsonMap(s.substring(start, i), map)
    }

    private fun parseArray(): List<Any?> {
        expect('[')
        val list = mutableListOf<Any?>()
        skipWs()
        if (peek() == ']') {
            i++
            return list
        }
        while (true) {
            list += parseValue()
            skipWs()
            when (peek()) {
                ',' -> i++
                ']' -> {
                    i++
                    break
                }
                else -> break
            }
        }
        return list
    }

    private fun parseString(): String {
        expect('"')
        val sb = StringBuilder()
        while (i < s.length) {
            val c = s[i++]
            when (c) {
                '"' -> break
                '\\' -> {
                    if (i >= s.length) break
                    when (val e = s[i++]) {
                        '"', '\\', '/' -> sb.append(e)
                        'n' -> sb.append('\n')
                        'r' -> sb.append('\r')
                        't' -> sb.append('\t')
                        'u' -> {
                            val hex = s.substring(i, (i + 4).coerceAtMost(s.length))
                            i += hex.length
                            sb.append(hex.toIntOrNull(16)?.toChar() ?: '?')
                        }
                        else -> sb.append(e)
                    }
                }
                else -> sb.append(c)
            }
        }
        return sb.toString()
    }

    private fun parseNumber(): Number {
        val start = i
        if (peek() == '-') i++
        while (peek() in '0'..'9') i++
        var isDouble = false
        if (peek() == '.') {
            isDouble = true
            i++
            while (peek() in '0'..'9') i++
        }
        if (peek() == 'e' || peek() == 'E') {
            isDouble = true
            i++
            if (peek() == '+' || peek() == '-') i++
            while (peek() in '0'..'9') i++
        }
        val num = s.substring(start, i)
        return if (isDouble) num.toDouble() else num.toLong()
    }

    private fun expect(ch: Char) {
        skipWs()
        if (i < s.length && s[i] == ch) i++
    }

    private fun expect(token: String) {
        skipWs()
        if (s.startsWith(token, i)) i += token.length
    }

    private fun peek(): Char = if (i < s.length) s[i] else '\u0000'

    private fun skipWs() {
        while (i < s.length && s[i].isWhitespace()) i++
    }
}

fun FormInspecao.temConteudoRascunho(): Boolean {
    if (tstResponsavel.isNotBlank()) return true
    if (obra.isNotBlank()) return true
    if (responsavelFrente.isNotBlank()) return true
    if (descricaoAtividade.isNotBlank()) return true
    if (efetivo.isNotBlank()) return true
    if (conclusao.isNotBlank()) return true
    if (informacoesAdicionais.isNotBlank() || informacoesAdicionaisFotos.isNotEmpty()) return true
    if (itensExtra.any { it.temConteudo() }) return true
    if (assinaturaSupervisor.isNotBlank() || assinaturaSesmt.isNotBlank()) return true
    if (itens.any { it.resposta != null || it.justificativa.isNotBlank() || it.fotos.isNotEmpty() }) return true
    if (desviosEncontrados.resposta != null || acoesCorretivas.resposta != null) return true
    return false
}
